// core
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';

// constants
import { RAPTOR_DEBOUNCE_TIME, RAPTOR_MEDICAL_VALUE_KEY } from '../../../constant/raptor-constants';
// pipes
import { RaptorCurrencyPipe } from '../../../pipes/raptor-currency.pipe';
import { RaptorDecimalPipe } from '../../../pipes/raptor-decimal.pipe';
// services
import { RaptorDataService } from '../../../services/raptor-data.service';
import { RaptorRepeaterService } from '../../../services/raptor-repeater.service';
import { RaptorPredicateService } from '../../../services/raptor-predicate.service';

@Component({
  selector: 'open-raptor-repeater-summary',
  templateUrl: './raptor-repeater-summary.component.html',
  styleUrls: ['./raptor-repeater-summary.component.scss']
})
export class RaptorRepeaterSummaryComponent implements OnInit {
  @Input() question: any;
  @Input() form: any;
  @Input() editing: any;
  @Output() save = new EventEmitter<any>();;

  questionControl: any;

  constructor(
    private raptorDataService: RaptorDataService,
    private raptorRepeaterService: RaptorRepeaterService,
    private raptorCurrencyPipe: RaptorCurrencyPipe,
    private raptorDecimalPipe: RaptorDecimalPipe,
    private raptorPredicateService: RaptorPredicateService
  ) { }

  ngOnInit(): void {
    this.questionControl = this.form.get(this.question.key);

    if (!this.raptorRepeaterService.isRepeaterValid(this.question.key)) {
      let invalidRow = this.raptorRepeaterService.getFirstInvalidRow(this.question.key);
      if (invalidRow && invalidRow.index > -1) {
        setTimeout(() => {
          // open up the first invalid repeater in the form
          this.edit(invalidRow.index);
            // call the repeater question save function so that the 
            // validation can run on the first invalid repeater
            this.save.emit();
        }, RAPTOR_DEBOUNCE_TIME);
      }
    }
  }

  public isRepeaterValid(key: string, i: number) {
    return this.raptorRepeaterService.isRowValid(this.raptorRepeaterService.repeaterValidationResults[key][i])
  }

  public edit(index: number) {
    let patchedValue: any = {};

    patchedValue[this.question.key] = 'edit_' + index;
    this.question.elements.forEach((element: any) => {
      const data = this.question.data[index][element.key];
      if (
        element.type === 'date' &&
        ['date_input_3_boxes', 'date_select_month_year'].indexOf(element.layout) > -1 &&
        typeof data === 'string'
      ) {
        patchedValue[element.key] = this.raptorDataService.convertToDateObject(data);
      } else {
        patchedValue[element.key] = data;
      }
    });
    this.form.patchValue(patchedValue, { emitEvent: true });
  }

  public delete(index: number) {
    let patchedValue: any = {};

    this.question.data.splice(index, 1);
    // NB: adding a date hash so that the question watcher in the repeater
    // will recognise a distinct until changed event when deleting the same row twice
    patchedValue[this.question.key] = 'delete_' + index + '_' + Date.now();
    this.form.patchValue(patchedValue, { emitEvent: true });
  }

  public getLabel(key: string) {
    let label;
    this.question.elements.forEach((element: any) => {
      if (element.key === key) {
        label = element.review_label || element.prompt;
      }
    });
    return label;
  }

  public formatValue(key: string, row: any, index: number) {
    let elements = this.question.elements.filter((element: { key: string; }) => element.key === key);
    let element;
    let data = row[key];
    
    // extra logic to handle 2 questions with the same key in show_if logic
    if (elements && elements.length) {
      if (elements.length > 1) {
        // find the visible element, assume there is a show_if on each of these
        // this is because we can have multiple questions with the same key inside of a repeater
        element = elements.find((element: any) => {
          if (element.show_if && typeof element.show_if === 'object') {
            return this.raptorPredicateService.validator(element.show_if, row); 
          } else {
            return element;
          }
        });
      } else if (elements.length === 1) {
        element = elements[0];
      }
    } else {
      element = elements;
    }

    if (element && data) {
      if (element.type === 'date' && ['date_input_3_boxes', 'date_select_month_year'].indexOf(element.layout) > -1) {
        data = this.raptorDataService.convertToDateString(row[key]) || row[key];
      }
      if (element.type === 'currency') {
        data = this.raptorCurrencyPipe.transform(element, row[key]);
      }
      if (element.type === 'number') {
        data = this.raptorDecimalPipe.transform(element, row[key]);
      }
      if (element.type === 'choice') {
        data = element.settings.choices.find((choice: any) => choice.value === row[key]).label;
      }
      if (element.type === 'medical_screening') {
        data = row[key + RAPTOR_MEDICAL_VALUE_KEY] ? row[key + RAPTOR_MEDICAL_VALUE_KEY].join(', ') : 'none';
      }
    }
    return data;
  }
}
